import { useRef, useEffect, useState } from 'react';
import { motion, useInView, useSpring, useTransform, useScroll } from 'framer-motion';
import { PhoneOff, UserX, Phone, TrendingDown } from 'lucide-react';

// Animated counter hook
const useAnimatedCounter = (end, duration = 2, inView = false, suffix = '', prefix = '') => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!inView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, inView]);

  return `${prefix}${count.toLocaleString()}${suffix}`;
};

// Animated stat card component
const AnimatedStatCard = ({ icon: Icon, stat, label, description, delay = 0, isInView }) => {
  const numericValue = parseFloat(stat.replace(/[^0-9.]/g, ''));
  const suffix = stat.includes('%') ? '%' : stat.includes('$') ? '' : '';
  const prefix = stat.includes('$') ? '$' : '';
  
  const animatedValue = useAnimatedCounter(numericValue, 1.5, isInView, suffix, prefix);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
      animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className="glass-card p-5 group hover:border-[#F04438]/30 transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        <motion.div 
          className="w-11 h-11 rounded-xl bg-[#F04438]/10 border border-[#F04438]/20 flex items-center justify-center flex-shrink-0"
          animate={isInView ? { 
            boxShadow: ['0 0 0 rgba(240,68,56,0)', '0 0 20px rgba(240,68,56,0.3)', '0 0 0 rgba(240,68,56,0)']
          } : {}}
          transition={{ duration: 2, repeat: Infinity, delay: delay + 1 }}
        >
          <Icon className="w-5 h-5 text-[#F04438]" />
        </motion.div>
        <div>
          <motion.div 
            className="text-2xl font-bold text-white mb-1"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.3, delay: delay + 0.3 }}
          >
            {isInView ? animatedValue : '0'}
          </motion.div>
          <div className="text-sm font-medium text-white/80 mb-1">{label}</div>
          <div className="text-xs text-white/50">{description}</div>
        </div>
      </div>
    </motion.div>
  );
};

// Floating missed call visualization
const MissedCallVisual = ({ isInView }) => {
  return (
    <div className="flex items-center justify-center gap-3 py-6">
      {[...Array(10)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={isInView ? {
            opacity: i < 4 ? [1, 0.3, 0] : [0, 1, 1],
            y: i < 4 ? [0, 20, 40] : [20, 0, 0],
            scale: i < 4 ? [1, 0.8, 0.5] : [0.5, 1, 1],
          } : {}}
          transition={{
            duration: 2.5,
            delay: i * 0.15,
            repeat: Infinity,
            repeatDelay: 2,
          }}
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            i < 4 ? 'bg-[#F04438]/80' : 'bg-[#32D583]/80'
          }`}
        >
          <Phone className="w-4 h-4 text-white" />
        </motion.div>
      ))}
    </div>
  );
};

const stats = [
  {
    icon: PhoneOff,
    stat: '27%',
    label: 'to 62% of calls go unanswered',
    description: 'Your crew is on job sites, office staff can\'t catch everything',
  },
  {
    icon: TrendingDown,
    stat: '85%',
    label: 'of missed callers never call back',
    description: 'They\'ve already moved on to your competitor',
  },
  {
    icon: UserX,
    stat: '$275',
    label: 'to $1,200 lost per missed call',
    description: 'Each unanswered call is potential revenue walking away',
  },
  {
    icon: PhoneOff,
    stat: '$126000',
    label: 'average yearly loss',
    description: 'The average home service business loses this to poor response',
  },
];

export const ProblemSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.15, 0]);

  return (
    <section
      ref={sectionRef}
      id="problem"
      className="py-24 relative overflow-hidden"
      data-testid="problem-section"
    >
      {/* Animated background glow */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: backgroundY }}
      >
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#F04438]/10 blur-[150px]"
          style={{ opacity: glowOpacity }}
        />
      </motion.div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Section header with blur-to-sharp animation */}
        <motion.div
          initial={{ opacity: 0, y: 40, filter: 'blur(20px)' }}
          animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center mb-12"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F04438]/10 border border-[#F04438]/20 mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-[#F04438]" />
            <span className="text-sm text-[#F04438]">The Real Problem</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
            data-testid="problem-headline"
          >
            Most Home Service Companies
            <br />
            <span className="text-[#F04438]">Don't Have a Lead Problem</span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-white/60 max-w-xl mx-auto"
          >
            They have a response problem. Calls get missed. Follow-up slips.
            Good leads go cold before anyone gets back to them.
          </motion.p>
        </motion.div>

        {/* Stats grid with staggered animation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8">
          {stats.map((item, index) => (
            <AnimatedStatCard
              key={index}
              icon={item.icon}
              stat={item.stat}
              label={item.label}
              description={item.description}
              delay={0.3 + index * 0.1}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Missed call visualization */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <MissedCallVisual isInView={isInView} />
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
            className="text-center text-sm text-white/40"
          >
            Every fading circle represents a missed opportunity walking away
          </motion.p>
        </motion.div>

        {/* Bottom summary pill */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.2 }}
          className="mt-10 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10">
            <span className="text-white/70">The problem usually isn't lead volume.</span>
            <span className="text-white font-semibold">It's response failure.</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
